import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { produce } from 'immer';

import * as ReportFilterActions from './report-filter.actions';
import { RMap, ReportTypes } from '@mode/shared/contract-common';
import { immRemoveKey, toDictionary } from '@mode/shared/util-js';
import { InjectionToken } from '@angular/core';

export const reportFilterFeatureKey = 'reportFilter';

export interface State {
  isFiltersLoading: boolean;
  isFiltersComplete: boolean;
  /**
   * Filter tokens ordered by their `row_order`
   */
  tokens: ReadonlyArray<string>;
  /**
   * Filter sans selections
   */
  filters: RMap<ReportTypes.ReportFilterDefinition>;
  /**
   * Current filter selections. May be a subset of the values keyed in to {@link selections} and
   * should be merged with {@link selections} in order to get the complete, current state
   * of filter selections. eg. `{  ...selections, ...currentSelections }`
   */
  currentSelections: RMap<ReportTypes.FilterSelection>;
  /**
   * Applied filter selections
   */
  selections: RMap<ReportTypes.FilterSelection>;
  /**
   * Persisted filter selections
   */
  defaults: RMap<ReportTypes.FilterSelection>;
  /**
   * Domain values for a filter field
   */
  options: RMap<ReportTypes.ReportFilterOptions>;

  // options status can come from flamingo state
}

export const initialState: State = {
  isFiltersLoading: false,
  isFiltersComplete: false,
  tokens: [],
  filters: {},
  currentSelections: {},
  selections: {},
  defaults: {},
  options: {},
};

function resourceToDefinition(resource: ReportTypes.ReportFilterResource): ReportTypes.ReportFilterDefinition {
  switch (resource.filter_type) {
    case ReportTypes.ReportFilterType.Multi:
    case ReportTypes.ReportFilterType.Single:
      return {
        controlType: resource.control_type,
        dataType: resource.data_type,
        filterType: resource.filter_type,
        formula: resource.formula,
        formulaType: resource.formula_type,
        name: resource.name,
        position: resource.row_order,
        token: resource.token,
        varType: resource.variable_type,
        hideSelectAll: resource.hide_select_all,
        showRelevantValuesOnly: resource.show_relevant_values_only,
      };
    case ReportTypes.ReportFilterType.Range:
      return {
        controlType: resource.control_type,
        dataType: resource.data_type,
        filterType: resource.filter_type,
        formula: resource.formula,
        formulaType: resource.formula_type,
        name: resource.name,
        position: resource.row_order,
        token: resource.token,
        varType: resource.variable_type,
        hideSelectAll: resource.hide_select_all,
        showRelevantValuesOnly: false,
      };
  }
}

function defaultFilterSelector(
  type: ReportTypes.ReportFilterType,
  oldSelection?: ReportTypes.FilterSelection
): ReportTypes.FilterSelection {
  switch (type) {
    case ReportTypes.ReportFilterType.Multi:
      // Transfer old single selection to multi selection
      if (oldSelection != null && 'value' in oldSelection && oldSelection.value !== undefined && !oldSelection.all) {
        return {
          all: false,
          values: [oldSelection.value],
        };
      } else {
        return {
          all: true,
          values: [],
        };
      }
    case ReportTypes.ReportFilterType.Range:
      return {
        max: null,
        min: null,
      };
    case ReportTypes.ReportFilterType.Single:
      if (oldSelection != null && 'all' in oldSelection) {
        return oldSelection;
      } else {
        return {
          all: true,
          value: undefined,
        };
      }
  }
}

export const isFiltersLoadingReducer = createReducer(
  initialState.isFiltersLoading,
  on(ReportFilterActions.loadReportFilters, (state, action) => true),
  on(
    ReportFilterActions.loadReportFiltersFailure,
    ReportFilterActions.loadReportFiltersSuccess,
    (state, action) => false
  )
);

export const isFiltersCompleteReducer = createReducer(
  initialState.isFiltersComplete,
  on(ReportFilterActions.loadReportFilters, (state, action) => false),
  on(
    ReportFilterActions.loadReportFiltersFailure,
    ReportFilterActions.loadReportFiltersSuccess,
    (state, action) => true
  )
);

export const tokensReducer = createReducer(
  initialState.tokens,
  on(
    ReportFilterActions.loadReportFiltersSuccess,
    ReportFilterActions.updateReportFilterPositionSuccess,
    (state, action) =>
      action.resources
        .slice(0)
        .sort((a, b) => a.row_order - b.row_order)
        .map((r) => r.token) // tokens state will maintain sort order
  ),
  on(ReportFilterActions.createReportFilterSuccess, (state, action) => state.concat(action.resource.token)),
  on(ReportFilterActions.destroyReportFilter, (state, action) => state.filter((t) => t !== action.token)),
  on(ReportFilterActions.destroyReportFilterFailure, (state, action) => state.concat(action.token))
);

export const filtersReducer = createReducer(
  initialState.filters,
  on(ReportFilterActions.loadReportFiltersSuccess, (state, action) =>
    action.resources.reduce(
      (acc, next) => {
        acc[next.token] = resourceToDefinition(next);
        return acc;
      },
      {} as { [token: string]: ReportTypes.ReportFilterDefinition }
    )
  ),
  on(ReportFilterActions.destroyReportFilterSuccess, (state, action) => immRemoveKey(state, action.token)),
  on(
    ReportFilterActions.createReportFilterSuccess,
    ReportFilterActions.updateReportFilterTypeSuccess,
    ReportFilterActions.updateReportFilterSuccess,
    ReportFilterActions.hideSelectAllSuccess,
    ReportFilterActions.showRelevantValuesSuccess,
    (state, action) => ({
      ...state,
      [action.resource.token]: resourceToDefinition(action.resource),
    })
  ),
  on(ReportFilterActions.updateReportFilterPositionSuccess, (state, action) =>
    produce(state, (draft) => {
      return action.resources.forEach((r) => {
        draft[r.token].position = r.row_order;
      });
    })
  )
);

export const selectionsReducer = createReducer(
  initialState.selections,
  on(
    ReportFilterActions.loadReportFiltersSuccess,
    ReportFilterActions.updateReportFilterDefaultsSuccess,
    (state, action) =>
      toDictionary(action.resources, (next) => {
        if (next.options.default != null) {
          switch (next.filter_type) {
            case ReportTypes.ReportFilterType.Single:
            case ReportTypes.ReportFilterType.Multi:
              return {
                ...next.options.default,
                // all might not be set on new filters, so add in a default value
                all: next.options.default.all ?? true,
              };
            default:
              return next.options.default;
          }
        } else {
          // Generally shouldn't arrive here, but here are some safe defaults otherwise
          switch (next.filter_type) {
            case ReportTypes.ReportFilterType.Range:
              return {
                min: null,
                max: null,
              };
            case ReportTypes.ReportFilterType.Multi:
              return {
                all: true,
                values: [],
              };
            case ReportTypes.ReportFilterType.Single:
              return {
                all: true,
                value: undefined,
              };
          }
        }
      })
  ),
  on(ReportFilterActions.hideSelectAllSuccess, (state, action) => {
    if (action.selection) {
      return {
        ...state,
        [action.resource.token]: action.selection,
      };
    }

    return state;
  }),
  on(ReportFilterActions.createReportFilterSuccess, (state, action) => ({
    ...state,
    [action.resource.token]: defaultFilterSelector(action.resource.filter_type),
  })),
  on(ReportFilterActions.destroyReportFilterSuccess, (state, action) => immRemoveKey(state, action.token)),
  on(ReportFilterActions.updateReportFilterTypeSuccess, (state, action) => ({
    // TODO: keep selection where applicable
    ...state,
    [action.resource.token]: defaultFilterSelector(action.resource.filter_type, action.selection),
  })),
  on(ReportFilterActions.applyReportFilters, (state, action) => ({
    ...state,
    ...action.selected, // Spreading to allow for only applying filters that have changed
  })),
  on(ReportFilterActions.resetReportFilter, (state, action) => ({
    ...state,
    [action.token]: action.defaults,
  })),
  on(ReportFilterActions.resetReportFilters, (state, action) => ({
    ...state,
    ...action.defaults,
  }))
);

export const currentSelectionsReducer = createReducer(
  initialState.currentSelections,
  on(
    ReportFilterActions.applyReportFilters,
    ReportFilterActions.resetReportFilters,
    ReportFilterActions.loadReportFiltersSuccess,
    ReportFilterActions.updateReportFilterTypeSuccess,
    ReportFilterActions.updateReportFilterDefaultsSuccess,
    () => {
      return {};
    }
  ),
  on(ReportFilterActions.resetReportFilter, (state, action) => immRemoveKey(state, action.token)),
  on(ReportFilterActions.destroyReportFilterSuccess, (state, action) => immRemoveKey(state, action.token)),
  on(ReportFilterActions.filterSelectionChange, (state, action) => ({
    ...state,
    [action.token]: action.selection,
  }))
);

export const defaultsReducer = createReducer(
  initialState.defaults,
  on(
    ReportFilterActions.loadReportFiltersSuccess,
    ReportFilterActions.updateReportFilterDefaultsSuccess,
    (state, action) => ({
      ...state,
      ...toDictionary(action.resources, (next) => {
        switch (next.filter_type) {
          case ReportTypes.ReportFilterType.Multi:
          case ReportTypes.ReportFilterType.Range:
          case ReportTypes.ReportFilterType.Single: {
            return {
              ...next.options.default,
              type: next.filter_type,
            };
          }
        }
      }),
    })
  ),
  on(ReportFilterActions.hideSelectAllSuccess, (state, action) => {
    if (action.resource.hide_select_all && action.selection && action.applyFilters) {
      return {
        ...state,
        [action.resource.token]: action.selection,
      };
    }

    return state;
  }),
  on(ReportFilterActions.createReportFilterSuccess, (state, action) => ({
    ...state,
    [action.resource.token]: defaultFilterSelector(action.resource.filter_type),
  })),
  on(ReportFilterActions.destroyReportFilterSuccess, (state, action) => immRemoveKey(state, action.token)),
  on(ReportFilterActions.updateReportFilterTypeSuccess, (state, action) => ({
    // TODO: keep defaults where applicable
    ...state,
    [action.resource.token]: defaultFilterSelector(action.resource.filter_type, action.selection),
  }))
);

export const optionsReducer = createReducer(
  initialState.options,

  on(ReportFilterActions.loadReportFilterOptsSuccess, (state, action) => ({ ...state, ...action.options })),
  on(ReportFilterActions.destroyReportFilterSuccess, (state, action) => immRemoveKey(state, action.token)),
  on(ReportFilterActions.updateReportFilterTypeSuccess, (state, action) => immRemoveKey(state, action.resource.token))
);

export const reducers = {
  tokens: tokensReducer,
  filters: filtersReducer,
  defaults: defaultsReducer,
  currentSelections: currentSelectionsReducer,
  selections: selectionsReducer,
  options: optionsReducer,
  isFiltersComplete: isFiltersCompleteReducer,
  isFiltersLoading: isFiltersLoadingReducer,
};

export const reducer = new InjectionToken<ActionReducerMap<State>>('reportFilter reducers token', {
  factory: () => reducers,
});
